import { Route } from '@angular/router';
import { EmptyLayoutComponent, PortalLayoutComponent } from '@twist/ui-layout';
import { authGuard } from '@twist/core';
import { OrganizationTypes } from '@twist/backbone-api-services/ciam';
import { NoAccessPageComponent } from './pages/no-access-page/no-access-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { TwistRouteData } from '@twist/ui-navigation';

export const appRoutes: Route[] = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: '',
    component: EmptyLayoutComponent,
    children: [
      {
        path: 'auth',
        loadChildren: () => import('@twist/authentication/feature-authentication').then((home) => home.AUTHENTICATION_ROUTES)
      },
      {
        path: 'routines',
        canActivate: [authGuard()],
        loadChildren: () => import('@twist/feature-routine-editor').then((m) => m.ROUTINE_ROUTES)
      },
      {
        path: 'installation-security',
        loadChildren: () =>
          import('@twist/feature-installation-security').then((m) => m.INSTALLATION_SECURITY_STANDALONE_ROUTES)
      }
    ]
  },
  {
    path: '',
    component: PortalLayoutComponent,
    children: [
      {
        path: 'dashboard',
        canActivate: [authGuard()],
        loadChildren: () => import('@twist/feature-dashboard').then((home) => home.DASHBOARD_ROUTES)
      },
      {
        path: 'installations',
        canActivate: [authGuard()],
        // `navId` lives on THIS segment — the one that owns the
        // `installations` url fragment — so the breadcrumb walker's
        // navId→descriptor-label fallback fires here (gated on a non-empty
        // routeURL). The lazily-loaded list page mounts at `{ path: '' }`,
        // whose empty routeURL keeps its own (inherited) navId suppressed,
        // so the "Installations" parent crumb is emitted exactly once on
        // `/installations`, `/installations/:id`, and its tab routes.
        data: { navId: 'installations' } satisfies TwistRouteData,
        loadChildren: () => import('@twist/feature-installations-list').then((m) => m.INSTALLATION_LIST_ROUTES)
      },
      {
        path: 'devices',
        canActivate: [authGuard()],
        loadChildren: () => import('@twist/feature-devices').then((m) => m.DEVICES_ROUTES)
      },
      {
        path: 'products',
        canActivate: [authGuard()],
        // No `navId` on THIS shell segment: `/products` redirects to
        // `/products/search`, and the URL-owning children carry their own
        // navIds (`search` → 'products-all', `blueprints` →
        // 'product-blueprints', `routine-blueprints` → 'routine-blueprints').
        // Putting 'products-all' here too would emit a duplicate "All
        // products" crumb on `/products/search` (the child already emits it
        // via the navId fallback on a non-empty routeURL).
        loadChildren: () => import('@twist/feature-products').then((m) => m.PRODUCTS_ROUTES)
      },
      {
        path: 'metadata',
        canActivate: [authGuard()],
        loadChildren: () => import('@twist/feature-metadata').then((m) => m.METADATA_ROUTES)
      },
      {
        path: 'my-organization',
        canActivate: [authGuard()],
        loadChildren: () => import('@twist/feature-my-organization').then((m) => m.MY_ORGANIZATION_ROUTES)
      },
      {
        path: 'manufacturing',
        canActivate: [authGuard({ orgTypes: [OrganizationTypes.TwistAdmin] })],
        loadChildren: () => import('@twist/feature-manufacturing').then((m) => m.MANUFACTURING_ROUTES)
      },
      {
        path: 'hardware-buildup',
        canActivate: [authGuard({ orgTypes: [OrganizationTypes.TwistAdmin] })],
        loadChildren: () => import('@twist/feature-hardware-buildup').then((m) => m.HARDWARE_BUILDUP_ROUTES)
      },
      {
        path: 'profile',
        canActivate: [authGuard()],
        loadChildren: () =>
          import('@twist/profile/feature-profile').then((m) => m.PROFILE_ROUTES)
      },
      {
        path: 'admin',
        canActivate: [authGuard({ orgTypes: [OrganizationTypes.TwistAdmin] })],
        loadChildren: () =>
          import('@twist/feature-admin').then((m) => m.ADMIN_ROUTES)
      },
      {
        path: 'summaries',
        canActivate: [authGuard({ orgTypes: [OrganizationTypes.TwistAdmin] })],
        loadChildren: () =>
          import('@twist/feature-summaries').then((m) => m.SUMMARIES_ROUTES)
      },
      {
        path: 'analytics',
        canActivate: [authGuard()],
        loadChildren: () =>
          import('@twist/feature-analytics').then((m) => m.ANALYTICS_ROUTES)
      }
    ]
  }, {
    path: '',
    children: [
      {
        path: 'errors/403',
        component: NoAccessPageComponent
      }
      ,
      {
        path: '**',
        component: NotFoundPageComponent
      }
    ]
  }
];
